import { PageShell } from '@/components/layout/PageShell'
import { data as withStatus, useLocation } from 'react-router'
import type { Route } from './+types/CmsPage'
import { SectionList, sectionQueries } from '@/components/blocks/BlockRenderer'
import { usePage } from '@/lib/cms/hooks'
import { createContentLoader } from '@/lib/cms/prefetch.server'
import { contentQueries } from '@/lib/cms/queries'
import { formatDate } from '@/lib/formatDate'
import { useTranslation } from '@/lib/i18n/LocaleContext'
import { messages } from '@/lib/i18n/messages'
import { localeFromParams } from '@/lib/routeLocale'
import { buildMeta, type PageSeo } from '@/lib/seo'
import NotFoundPage from '@/pages/NotFoundPage'

/** The CMS page slug is the route's last path segment (e.g. `about`). */
function slugFromPath(pathname: string): string {
  return pathname.replace(/\/+$/, '').split('/').pop() ?? ''
}

export async function loader({ params, url }: Route.LoaderArgs) {
  const locale = localeFromParams(params)
  const content = createContentLoader(locale)
  // A CMS failure propagates (error boundary, 5xx); a missing doc is a real 404.
  const page = await content.fetch(contentQueries.page(slugFromPath(url.pathname)))
  if (!page) {
    const seo: PageSeo = { title: messages[locale].pageTitles.notFound, noindex: true }
    return withStatus({ notFound: true, dehydratedState: content.dehydrate(), seo }, { status: 404 })
  }
  await content.prefetch(sectionQueries(page.sections))
  const seo: PageSeo = { title: page.seoTitle ?? page.title, description: page.seoDescription }
  return { notFound: false, dehydratedState: content.dehydrate(), seo }
}

export const meta: Route.MetaFunction = (args) => buildMeta(args, args.data?.seo)

export default function CmsPageRoute({ loaderData }: Route.ComponentProps) {
  const { pathname } = useLocation()
  return loaderData.notFound ? <NotFoundPage /> : <CmsPage slug={slugFromPath(pathname)} />
}

/** Generic CMS-driven page: renders a Pages doc's `sections` by slug. */
function CmsPage({ slug }: { slug: string }) {
  const { t, locale } = useTranslation()
  const { data, loading, error } = usePage(slug)
  const updated = formatDate(data?.updatedAt, locale)

  return (
    <PageShell>
      {loading && (
        <section className="block">
          <div className="wrap"><p className="muted">{t.common.loading}</p></div>
        </section>
      )}

      {error && (
        <section className="block">
          <div className="wrap"><p className="error-text">{t.common.error}</p></div>
        </section>
      )}

      {data && <SectionList sections={data.sections} />}

      {updated && (
        <div className="wrap wrap-narrow">
          <p className="muted small">
            {t.common.lastUpdated}: <time dateTime={data?.updatedAt}>{updated}</time>
          </p>
        </div>
      )}
    </PageShell>
  )
}
